import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { LocationProvider } from '@reach/router';
import styled from 'styled-components';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import devices from '../../../../../js/styles/Devices';

import ExtraFormFunctionalityWrapper from '../../ExtraFormFunctionalityWrapper';
import Form from '../../../../common/Form';
import NewFormBlock from '../../../NewFormBlock';
import Disclaimer from '../../../../utils/Disclaimer/Disclaimer';
import WarningDisclaimerWithIntl from '../../../../utils/Disclaimer/WarningDisclaimer';

const Wrapper = styled.div`
  && {
    position: relative;
    text-align: left;
    padding-top: 0;
    max-width: 36rem;
    margin: auto;
    border-radius: 20px;
    background-color: var(--white);
    box-shadow: var(--form-shadow);
    & > * > .onb .advisa {
      width: auto!important;
    }
  }
`;

const FormBlockWrap = styled.div`
  && {
    position: relative;
    background: var(--white);
    text-align: left;
    border-radius: 4px;
    box-shadow: var(--form-shadow);
    padding-top: 0;
    max-width: 36rem;
    margin: auto;
  }
`;

const DisclaimerWrapper = styled.div`
  background: linear-gradient(to right, transparent, #E6E6E6 30%, #E6E6E6 70%, transparent);
  padding: 20px;
  background-size: 100% 1px;
  background-repeat: no-repeat;
  background-position: top;
  background-color: var(--white);
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
`;

const FormFrame = styled.div`
  && {
    margin: auto;
    margin-top: auto;

    @media ${devices.downFromTablet} {
      margin-top: auto;
    }

    @media ${devices.downFromMobile} {
      width: 100%;
    }
  }
`;

const FormBlock = ({
  show,
  disclaimer,
  floating,
  fullWidth,
  forwardRef,
  exampleLoan,
  title,
  partnerForm,
  prefilledApplicationForm,
  prefilledValues,
  subtitle,
  type,
  prefilledValuesNewForm,
  ...rest
}) => {
  const isFeatureEnabled = useFeatureIsOn('new-onb20');

  useEffect(() => {
    if (typeof window !== 'undefined' && window.hj && isFeatureEnabled) {
      window.hj('event', 'new_form');
    }
  }, [isFeatureEnabled]);

  if (!show) return null;

  const renderForm = (context) => (
    <Form
      key="form-block"
      exampleLoan={exampleLoan}
      title={title}
      subtitle={subtitle}
      location={context.location}
      partnerForm={partnerForm}
      prefilledApplicationForm={prefilledApplicationForm}
      prefilledValues={prefilledValues}
      type={type[0]}
      {...rest}
    />
  );

  const formComponent = isFeatureEnabled ? (
    <NewFormBlock prefilledValuesNewForm />
  ) : (
    <>
      <FormBlockWrap
        ref={forwardRef}
        id="form"
      >
        <FormFrame>
          <ExtraFormFunctionalityWrapper>
            <LocationProvider>
              {(context) => renderForm(context)}
            </LocationProvider>
          </ExtraFormFunctionalityWrapper>
        </FormFrame>
      </FormBlockWrap>
      <Disclaimer />
    </>
  );

  return (
    <Wrapper>
      {formComponent}
      {isFeatureEnabled ? (
        <DisclaimerWrapper>
          <WarningDisclaimerWithIntl />
        </DisclaimerWrapper>
      ) : <WarningDisclaimerWithIntl />}
    </Wrapper>
  );
};

FormBlock.propTypes = {
  show: PropTypes.bool,
  disclaimer: PropTypes.string,
  inputSource: PropTypes.string,
  advisaSource: PropTypes.string,
  floating: PropTypes.shape(),
  fullWidth: PropTypes.bool,
  forwardRef: PropTypes.shape(),
  exampleLoan: PropTypes.shape(),
  title: PropTypes.string,
  partnerForm: PropTypes.bool,
  prefilledApplicationForm: PropTypes.bool,
  prefilledValues: PropTypes.shape(),
  prefilledValuesNewForm: PropTypes.shape(),
  subtitle: PropTypes.string,
  type: PropTypes.arrayOf(PropTypes.string),
};

FormBlock.defaultProps = {
  show: true,
  type: [''],
  disclaimer: null,
  inputSource: '',
  advisaSource: '',
  floating: {},
  fullWidth: false,
  forwardRef: null,
  exampleLoan: null,
  title: null,
  partnerForm: false,
  prefilledApplicationForm: false,
  prefilledValues: {},
  prefilledValuesNewForm: {},
  subtitle: null,
};

export default FormBlock;

export const formBlockQuery = graphql`
  fragment formBlockFields on Form {
    show
    type
    title
    subtitle
    type
    inputSource
    advisaSource
  }
`;
